// View mesa summary and sync with POS

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../config/firebase/config';
import { useRestaurant } from '../../../providers/RestaurantContext';
import { usePOSService } from '../../../hooks/usePOSService';
import { useAutoSync } from '../../../hooks/useAutoSync';
import { RefreshCw, Eye, Clock, Users, LogOut, ArrowLeft, Trash2, Receipt, Home } from 'lucide-react';
import { NotificationBell } from '../components/NotificationBell';
import { signOut } from 'firebase/auth';
import { auth } from '../../../config/firebase/auth';
import { deleteDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Logo from '../../../assets/fonts/Solo-logo.png';

// Format currency with thousand separators
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

interface MesaItem {
  name: string;
  price: number;
  quantity: number;
  note?: string;
  paid?: boolean;
  paidByTransactionId?: string | null;
}

interface MesaData {
  numeroMesa: string;
  items: MesaItem[];
  status?: string;
  createdAt?: any;
  ultimaActualizacion?: any;
  customerCount?: number;
  posTicketId?: string;
  externalTicketId?: string;
  montoTotal?: number;
  montoTotalConImpuestos?: number;
  montoPagado?: number;
  restante?: number;
}

const MesaSummaryScreen: React.FC = () => {
  const { id: mesaId } = useParams();
  const navigate = useNavigate();
  const { restaurant } = useRestaurant();
  const [mesaData, setMesaData] = useState<MesaData | null>(null);
  const [isAutoSyncing, setIsAutoSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update clock every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  // Load mesa from restaurant-scoped path; when session-driven, merge active session items/totals
  useEffect(() => {
    if (!mesaId || !restaurant?.id) return;

    const mesaRef = doc(db, 'restaurants', restaurant.id, 'mesas', mesaId);
    let unsubSession: (() => void) | null = null;

    const unsubscribe = onSnapshot(mesaRef, (snapshot) => {
      if (!snapshot.exists()) return;

      const data = snapshot.data();
      const base: MesaData = {
        numeroMesa: data.numeroMesa || '',
        items: data.items || [],
        status: data.status || 'active',
        createdAt: data.createdAt,
        ultimaActualizacion: data.ultimaActualizacion,
        customerCount: data.customerCount || 0,
        posTicketId: data.posTicketId,
        externalTicketId: data.externalTicketId,
        montoTotal: data.montoTotal ?? 0,
        montoTotalConImpuestos: data.montoTotalConImpuestos ?? undefined,
        montoPagado: data.montoPagado ?? 0,
        restante: data.restante ?? 0,
      };

      const activeSessionId = data.activeSessionId;
      if (typeof activeSessionId !== 'string' || !activeSessionId) {
        unsubSession?.();
        unsubSession = null;
        setMesaData(base);
        return;
      }

      unsubSession?.();
      const sessionRef = doc(mesaRef, 'sessions', activeSessionId);
      unsubSession = onSnapshot(sessionRef, (sessionSnap) => {
        if (!sessionSnap.exists()) {
          setMesaData(base);
          return;
        }
        const s = sessionSnap.data();
        setMesaData({
          ...base,
          items: s.items ?? base.items,
          montoTotal: s.montoTotal ?? base.montoTotal,
          montoTotalConImpuestos: s.montoTotalConImpuestos ?? base.montoTotalConImpuestos,
          montoPagado: s.montoPagado ?? base.montoPagado,
          restante: s.restante ?? base.restante,
          status: s.estado === 'cerrada' ? 'cerrada' : (s.estado === 'abierta' ? 'ocupada' : base.status),
        });
      });
    });

    return () => {
      unsubscribe();
      unsubSession?.();
    };
  }, [mesaId, restaurant?.id]);
  
  // Initialize POS service
  const { posService } = usePOSService(restaurant?.id, restaurant?.posConfig);

  // Auto-sync with POS every 30 seconds
  const { syncNow } = useAutoSync({
    mesaId: mesaId || '',
    posService,
    enabled: !!mesaId && !!posService && restaurant?.posConfig?.syncSettings?.autoSync !== false,
    intervalSeconds: 30,
    onSyncStart: () => setIsAutoSyncing(true),
    onSyncComplete: (success) => {
      setIsAutoSyncing(false);
      if (success) {
        setLastSyncTime(new Date());
        // Updates happen silently in background
      }
    }
  });

  const handleSyncNow = () => {
    if (syncNow) {
      syncNow();
    }
  };

  // Total con impuesto: use synced value when present, else sum of items + tax
  const totalAmount = mesaData?.items?.reduce((sum, item) => sum + item.quantity * item.price, 0) || 0;
  const taxRate = restaurant?.settings?.taxRate || 18; // Default 18%
  const taxAmount = totalAmount * (taxRate / 100);
  const totalWithTax = mesaData?.montoTotalConImpuestos ?? (totalAmount + taxAmount);
  // Pagado comes from Firestore (mesa.montoPagado). New payments store full amount (subtotal+tax+tip); older data may show only subtotal.
  const montoPagado = mesaData?.montoPagado ?? 0;
  // Restante: show what's still owed so Total con impuesto = Pagado + Restante (no hardcoding; allows paying remainder)
  const restante = Math.max(0, totalWithTax - montoPagado);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleDeleteMesa = async () => {
    if (!mesaId) return;

    // Confirmation dialog
    const confirmed = window.confirm(
      `¿Estás seguro de que deseas eliminar la Mesa ${mesaData?.numeroMesa}?\n\nEsta acción no se puede deshacer.`
    );

    if (!confirmed) return;

    if (!restaurant?.id) {
      toast.error('No se pudo identificar el restaurante');
      return;
    }
    try {
      const mesaRef = doc(db, 'restaurants', restaurant.id, 'mesas', mesaId);
      await deleteDoc(mesaRef);
      
      toast.success(`Mesa ${mesaData?.numeroMesa} eliminada exitosamente`);
      
      // Navigate back to mesas screen
      navigate('/admin/mesas');
    } catch (error: any) {
      console.error('Error deleting mesa:', error);
      toast.error(`Error al eliminar mesa: ${error.message}`);
    }
  };

  if (!mesaData) {
    return (
      <div className="min-h-screen w-full flex flex-col px-6 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-zinc-400" />
            <p className="text-zinc-500">Cargando datos de la mesa...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col px-6 py-6 animate-fade-in">
      {/* Header Bar */}
      <div className="flex items-center justify-between mb-8 w-full relative">
        {/* Left Side - Logo + YAP Text */}
        <div className="flex items-center">
          <Link to="/admin/mesas" className="flex items-center gap-3 group">
            <img 
              src={Logo} 
              alt="YAP Logo" 
              className="h-10 w-auto object-contain"
            />
            <span className="text-3xl font-bold tracking-tighter text-white group-hover:text-yap-primary transition-colors">
              yap
            </span>
          </Link>
        </div>

        {/* Center - Clock Display */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-zinc-900/40 backdrop-blur-xl border border-white/5 rounded-full">
          <Clock size={16} className="text-zinc-400" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-white leading-none">
              {currentTime.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="text-[10px] text-zinc-500 font-medium leading-none mt-0.5">
              {currentTime.toLocaleDateString('es-DO', { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
          </div>
        </div>

        {/* Right Side - Volver a inicio + Bell + Logout */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin"
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors font-medium"
          >
            <Home size={18} />
            <span>Volver a inicio</span>
          </Link>
          <div className="w-px h-6 bg-zinc-700" />
          <NotificationBell />
          <div className="w-px h-6 bg-zinc-700"></div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-red-500 transition-colors font-medium"
          >
            <LogOut size={18} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">Mesa {mesaData.numeroMesa}</h1>
            <div className="flex items-center gap-4 mt-3 text-sm text-zinc-400">
              <div className="flex items-center gap-2 bg-zinc-900/40 backdrop-blur-xl px-3 py-1.5 rounded-full border border-white/5">
                <Users className="w-4 h-4" />
                <span>{mesaData.customerCount || 0} clientes</span>
              </div>
              <div className="flex items-center gap-2 bg-zinc-900/40 backdrop-blur-xl px-3 py-1.5 rounded-full border border-white/5">
                <Clock className="w-4 h-4" />
                <span>{lastSyncTime ? lastSyncTime.toLocaleTimeString() : 'Nunca'}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/mesas')}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-900/40 backdrop-blur-xl border border-white/10 text-white rounded-full hover:bg-zinc-800/70 hover:border-yap-primary/40 transition-all text-sm font-semibold shadow-md hover:shadow-[0_0_16px_-6px_rgba(0,255,132,0.25)]"
            >
              <ArrowLeft size={16} />
              <span>Volver al Panel de Mesas</span>
            </button>
          </div>
        </div>

        {/* Mesa Status */}
        <div className="bg-zinc-900/40 backdrop-blur-xl rounded-2xl border border-white/5 p-5 mb-6 transition-all duration-300 hover:bg-zinc-800/60 hover:border-yap-primary/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${
                mesaData.status === 'active' || mesaData.status === 'ocupada' ? 'bg-yellow-500' : 
                mesaData.status === 'cerrada' ? 'bg-zinc-500' : 'bg-yap-primary'
              }`}></div>
              <span className="font-semibold text-white">Estado: {mesaData.status === 'active' ? 'Activa' : (mesaData.status || 'Activa')}</span>
            </div>
            <div className="text-sm text-zinc-400 font-medium">
              {mesaData.ultimaActualizacion ? 
                `Actualizado: ${
                  mesaData.ultimaActualizacion.seconds 
                    ? new Date(mesaData.ultimaActualizacion.seconds * 1000).toLocaleString()
                    : typeof mesaData.ultimaActualizacion === 'string'
                    ? new Date(mesaData.ultimaActualizacion).toLocaleString()
                    : 'Fecha inválida'
                }` : 
                'Sin actualizaciones'
              }
            </div>
          </div>
          
          {/* POS Ticket ID */}
          {(mesaData.posTicketId || mesaData.externalTicketId) && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <div className="text-sm text-zinc-400">
                <span className="font-medium">POS Ticket ID:</span>{' '}
                <span className="text-zinc-300 font-mono text-xs">
                  {mesaData.posTicketId || mesaData.externalTicketId}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Items List */}
        {mesaData.items.length === 0 ? (
          <div className="bg-zinc-900/40 backdrop-blur-xl rounded-xl border border-white/5 p-5 text-center transition-all duration-300 hover:bg-zinc-800/60 hover:border-yap-primary/30">
            <Eye className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
            <p className="text-zinc-300 text-base font-semibold">Esta mesa no tiene ítems registrados</p>
            <p className="text-zinc-500 text-xs mt-1.5 font-medium">Los ítems aparecerán aquí cuando se sincronicen desde el POS</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Items */}
            <div className="bg-zinc-900/40 backdrop-blur-xl rounded-xl border border-white/5 overflow-hidden transition-all duration-300 hover:bg-zinc-800/60 hover:border-yap-primary/30">
              <div className="px-4 py-3 border-b border-white/5 bg-zinc-900/20 flex items-center justify-between">
                <h3 className="text-base font-bold text-white tracking-tight">Ítems de la Mesa</h3>
                <Link
                  to={`/admin/mesa/${mesaId}/transactions`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-800/80 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors text-xs font-semibold"
                >
                  <Receipt className="w-3.5 h-3.5" />
                  Ver transacciones
                </Link>
              </div>
              <div className="divide-y divide-white/5">
                {mesaData.items.map((item, index) => (
                  <div key={index} className="px-4 py-3 hover:bg-zinc-800/40 transition-colors">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-white text-sm">{item.name}</h4>
                        {item.note && (
                          <p className="text-xs text-zinc-400 mt-1 font-medium">Nota: {item.note}</p>
                        )}
                        {item.paid && (
                          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-900/40 text-green-400 border border-green-500/30">
                              Pagado
                            </span>
                            {item.paidByTransactionId && (
                              <Link
                                to={`/admin/mesa/${mesaId}/transactions`}
                                className="text-[10px] text-yap-primary hover:underline font-medium"
                              >
                                Transacción
                              </Link>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs text-zinc-400 font-medium">
                          {item.quantity} × RD${formatCurrency(item.price)}
                        </div>
                        <div className="font-semibold text-yap-primary text-sm mt-0.5">
                          RD${formatCurrency(item.quantity * item.price)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary - compact, premium */}
            <div className="bg-zinc-900/40 backdrop-blur-xl rounded-xl border border-white/5 p-4 transition-all duration-300 hover:bg-zinc-800/60 hover:border-yap-primary/30">
              <h3 className="text-base font-bold text-white mb-3 tracking-tight">Resumen de Cuenta</h3>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-zinc-400">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-semibold text-zinc-300">RD${formatCurrency(totalAmount)}</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span className="font-medium">Impuesto ({taxRate}%)</span>
                  <span className="font-semibold text-zinc-300">RD${formatCurrency(taxAmount)}</span>
                </div>
                <div className="flex justify-between text-zinc-300 pt-1.5 border-t border-white/5">
                  <span className="font-semibold">Total con impuesto</span>
                  <span className="font-bold text-yap-primary">RD${formatCurrency(totalWithTax)}</span>
                </div>
                <div className="flex justify-between text-green-400 pt-1.5 border-t border-white/5">
                  <span className="font-medium">Pagado</span>
                  <span className="font-semibold">RD${formatCurrency(montoPagado)}</span>
                </div>
                <div className="flex justify-between text-red-400">
                  <span className="font-medium">Restante</span>
                  <span className="font-semibold">RD${formatCurrency(restante)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end items-center mt-8">
          <button
            onClick={handleDeleteMesa}
            className="flex items-center gap-2 px-6 py-3 bg-red-900/40 backdrop-blur-xl border border-red-500/30 text-red-400 rounded-full hover:bg-red-800/60 hover:border-red-500/50 hover:text-red-300 transition-all font-semibold shadow-lg hover:shadow-[0_0_20px_-5px_rgba(239,68,68,0.3)]"
          >
            <Trash2 size={18} />
            Eliminar Mesa
          </button>
        </div>
      </div>
    </div>
  );
};

export default MesaSummaryScreen;
